<template>
    <div class="file-preview" :class="`is-${previewType}`">
        <div v-if="previewType === 'empty'" class="file-preview__state text-muted text-center w-100">
            Ingen fil vald.
        </div>

        <div v-else-if="previewType === 'image'" class="file-preview__image-wrapper d-flex align-items-center justify-content-center w-100">
            <img
                :src="resolvedSource"
                :alt="resolvedFileName"
                class="file-preview__image"
                :style="{ maxHeight }"
            >
        </div>

        <div v-else-if="previewType === 'pdf'" class="file-preview__pdf-wrapper w-100">
            <iframe
                :src="resolvedSource"
                :title="resolvedFileName"
                class="file-preview__pdf w-100 border-0"
                :style="{ height: maxHeight }"
            />
        </div>

        <div v-else-if="previewType === 'loading'" class="file-preview__state text-muted text-center w-100">
            Läser fil...
        </div>

        <div v-else-if="previewType === 'error'" class="file-preview__state text-danger text-center w-100">
            {{ loadError }}
        </div>

<!--        <div v-else-if="previewType === 'markdown'" class="file-preview__text w-100 p-3 border rounded" :style="{ maxHeight }">-->
<!--            <MarkdownRenderer :source="textContent" />-->
<!--        </div>-->

        <div
            v-else-if="previewType === 'text'"
            class="file-preview__text file-preview__text--plain w-100 p-3 mb-0 border rounded"
            :style="{ maxHeight }"
            v-html="plainTextHtml"
        />

        <div v-else class="file-preview__state text-center w-100 p-4">
            <div class="font-weight-bold mb-1">{{ resolvedFileName }}</div>
            <div class="text-muted mb-2">
                Förhandsvisning stöds inte för denna filtyp.
            </div>
            <a
                v-if="resolvedSource"
                :href="resolvedDownloadUrl"
                target="_blank"
                rel="noopener"
                class="btn btn-outline-primary btn-sm"
            >
                Öppna fil
            </a>
        </div>
    </div>
</template>

<script>
const IMAGE_EXTENSIONS = ['apng', 'avif', 'bmp', 'gif', 'heic', 'heif', 'ico', 'jpeg', 'jpg', 'png', 'svg', 'webp'];
const TEXT_EXTENSIONS = ['csv', 'htm', 'html', 'json', 'log', 'sql', 'text', 'toml', 'tsv', 'txt', 'xml', 'yaml', 'yml'];
const MARKDOWN_EXTENSIONS = ['markdown', 'md', 'mdown'];

function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export default {
    props: {
        file: {
            type: Object,
            default: null,
        },
        src: {
            type: String,
            default: null,
        },
        content: {
            type: String,
            default: null,
        },
        mimeType: {
            type: String,
            default: null,
        },
        extension: {
            type: String,
            default: null,
        },
        fileName: {
            type: String,
            default: null,
        },
        downloadUrl: {
            type: String,
            default: null,
        },
        maxHeight: {
            type: String,
            default: '70vh',
        },
    },
    data() {
        return {
            textContent: this.content,
            loadError: null,
            activeTextRequest: null,
            textRequestId: 0,
        };
    },
    computed: {
        resolvedSource() {
            return this.src
                || this.file?.url_show
                || this.file?.url_download
                || '';
        },
        resolvedDownloadUrl() {
            return this.downloadUrl || this.resolvedSource;
        },
        resolvedMimeType() {
            return (this.mimeType
                || this.file?.mime_type
                || this.file?.mime
                || '').toLowerCase();
        },
        resolvedExtension() {
            const explicitExtension = this.extension || this.file?.fileext;

            if (explicitExtension) {
                return explicitExtension.toLowerCase().replace(/^\./, '');
            }

            const explicitName = this.fileName || this.file?.filename;
            const sourceName = this.resolvedSource.split('?')[0];
            const fileName = explicitName || sourceName.split('/').pop() || '';
            const parts = fileName.split('.');

            return parts.length > 1 ? parts.pop().toLowerCase() : '';
        },
        resolvedFileName() {
            if (this.fileName) {
                return this.fileName;
            }

            if (this.file?.filename) {
                return this.file.filename;
            }

            if (this.resolvedSource) {
                return this.resolvedSource.split('?')[0].split('/').pop() || 'Fil';
            }

            return 'Fil';
        },
        plainTextHtml() {
            return escapeHtml(this.textContent).replace(/\r\n|\r|\n/g, '<br>');
        },
        isImage() {
            return this.resolvedMimeType.startsWith('image/')
                || IMAGE_EXTENSIONS.includes(this.resolvedExtension);
        },
        isPdf() {
            return this.resolvedMimeType === 'application/pdf'
                || this.resolvedExtension === 'pdf';
        },
        isMarkdown() {
            return MARKDOWN_EXTENSIONS.includes(this.resolvedExtension)
                || ['text/markdown', 'text/x-markdown'].includes(this.resolvedMimeType);
        },
        isText() {
            return this.isMarkdown
                || this.resolvedMimeType.startsWith('text/')
                || ['application/json', 'application/xml', 'application/x-yaml'].includes(this.resolvedMimeType)
                || TEXT_EXTENSIONS.includes(this.resolvedExtension);
        },
        previewType() {
            if (!this.resolvedSource && !this.content) {
                return 'empty';
            }

            if (this.isImage) {
                return 'image';
            }

            if (this.isPdf) {
                return 'pdf';
            }

            if (this.isText && !this.textContent && this.resolvedSource && !this.loadError) {
                return 'loading';
            }

            if (this.loadError) {
                return 'error';
            }

            // if (this.isMarkdown) {
            //     return 'markdown';
            // }

            if (this.isText) {
                return 'text';
            }

            return 'unsupported';
        },
    },
    watch: {
        content: 'loadTextContent',
        resolvedSource: 'loadTextContent',
        resolvedMimeType: 'loadTextContent',
        resolvedExtension: 'loadTextContent',
    },
    created() {
        this.loadTextContent();
    },
    beforeDestroy() {
        if (this.activeTextRequest) {
            this.activeTextRequest.abort();
            this.activeTextRequest = null;
        }
    },
    methods: {
        async loadTextContent() {
            this.textRequestId += 1;
            const currentRequestId = this.textRequestId;

            if (this.activeTextRequest) {
                this.activeTextRequest.abort();
                this.activeTextRequest = null;
            }

            if (!this.isText) {
                this.textContent = '';
                this.loadError = '';
                return;
            }

            if (this.content) {
                this.textContent = this.content;
                this.loadError = '';
                return;
            }

            if (!this.resolvedSource) {
                this.textContent = '';
                this.loadError = '';
                return;
            }

            this.loadError = '';

            const controller = new AbortController();
            this.activeTextRequest = controller;

            try {
                const response = await fetch(this.resolvedSource, {
                    signal: controller.signal,
                    headers: {
                        Accept: 'text/plain, text/markdown, text/csv, application/json, application/xml;q=0.9, */*;q=0.1',
                    },
                });

                if (!response.ok) {
                    throw new Error(`Kunde inte läsa filen (${response.status}).`);
                }

                const content = await response.text();

                if (currentRequestId !== this.textRequestId) {
                    return;
                }

                this.textContent = content;
                this.activeTextRequest = null;
            } catch (error) {
                if (error?.name === 'AbortError') {
                    return;
                }

                if (currentRequestId !== this.textRequestId) {
                    return;
                }

                this.textContent = '';
                this.loadError = error instanceof Error ? error.message : 'Kunde inte läsa filen.';
                this.activeTextRequest = null;
            }
        },
    },
};
</script>

<style scoped>
.file-preview {
    overflow: hidden;
}

.file-preview__image {
    display: block;
    max-width: 100%;
    width: auto;
    object-fit: contain;
}

.file-preview__pdf {
    display: block;
    min-height: 320px;
}

.file-preview__text {
    overflow: auto;
}

.file-preview__text--plain {
    white-space: pre-wrap;
    word-break: break-word;
    tab-size: 4;
    font-size: 13px;
    line-height: 1.5;
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace;
}
</style>
